import { useCallback, useEffect, useRef, useState } from 'react';

// Speech-to-text using the browser's built-in Web Speech API (webkitSpeechRecognition). Supported
// in Chrome, Edge, and Safari. Firefox/Opera is only about 2% of our users
// Firefox has no support; the mic button is simply hidden there (see speechToTextSupported).
interface SpeechRecognitionAlternative {
  readonly transcript: string;
}

interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  readonly [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent {
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

type SpeechRecognitionCtor = new () => SpeechRecognition;

const STOP_BEFORE_SPEECH_MS = 8_000;
const STOP_AFTER_SPEECH_MS = 3_000;

interface Options {
  // The text already in the box being dictated into. When given, what is said is added after the text
  // the box held when recording started, and onFinish gets the box's text as it stands when recording ends.
  text?: string;
  onTranscript: (dictated: string) => void;
  onFinish: (draft: string) => void;
}

const speechRecognitionCtor = (): SpeechRecognitionCtor | undefined => {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
};

export const speechToTextSupported = (): boolean => {
  return typeof window !== 'undefined' && !!speechRecognitionCtor();
};

export function useSpeechToText({ text, onTranscript, onFinish }: Options) {
  const [recording, setRecording] = useState(false);

  const textRef = useRef(text);
  textRef.current = text;

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const transcriptRef = useRef('');
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearStopTimer = useCallback(() => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    const Ctor = speechRecognitionCtor();
    if (!Ctor || recognitionRef.current) return;

    const recognition = new Ctor();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    transcriptRef.current = '';
    const addsToExistingText = textRef.current !== undefined;
    const textBeforeDictation = textRef.current?.trim() ?? '';

    const armStopTimer = (ms: number) => {
      clearStopTimer();
      stopTimerRef.current = setTimeout(() => recognition.stop(), ms);
    };

    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      transcriptRef.current = transcript.trim();

      const dictated = [textBeforeDictation, transcriptRef.current].filter(Boolean).join(' ');
      // onend can fire before the parent re-renders with the dictated text.
      if (addsToExistingText) textRef.current = dictated;
      onTranscript(dictated);

      armStopTimer(STOP_AFTER_SPEECH_MS);
    };

    recognition.onerror = null;

    recognition.onend = () => {
      clearStopTimer();
      recognitionRef.current = null;
      setRecording(false);

      const transcript = transcriptRef.current.trim();
      transcriptRef.current = '';
      if (transcript) onFinish(addsToExistingText ? (textRef.current ?? '').trim() : transcript);
    };

    recognitionRef.current = recognition;
    setRecording(true);
    recognition.start();
    armStopTimer(STOP_BEFORE_SPEECH_MS);
  }, [onTranscript, onFinish, clearStopTimer]);

  const toggle = useCallback(() => {
    if (recording) {
      stop();
    } else {
      start();
    }
  }, [recording, start, stop]);

  // Aborts without submitting: detach the handlers first so onend can't fire a submit,
  // then stop, drop the timer, and discard the transcript.
  const cancel = useCallback(() => {
    clearStopTimer();

    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.onresult = null;
      recognition.onend = null;
      recognition.stop();
      recognitionRef.current = null;
    }

    transcriptRef.current = '';
    setRecording(false);
  }, [clearStopTimer]);

  useEffect(() => cancel, [cancel]);

  return { cancel, recording, toggle };
}
